import { RoundedFilledButton } from '@/components/buttons/Buttons';
import { LineDivider } from '@/components/utils/Divider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdFullscreen } from 'react-icons/md';
import RouteDetails from '../../../modules/home/route-details/RouteDetails';
import ExpandedMapModal from '../../../modules/home/views/expanded-map/ExpandedMapModal';
import styles from './row-expansion-template.module.css';
import { X4bActivityDto, ActivityHelper } from '@/modules/vehicle/engine/X4bActivityDto';
import { formatCurrency } from '@/common/common_utils';
import { DateHelper } from '@/common/DateHelper';
import { BaseResponse } from '@/modules/authentication/engine/authentication.types';
import { showSuccess, showError } from '@/modules/notification/engine/notification.library';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { getVehicleTypeInfo } from '@/modules/vehicle/engine/VehicleType';
import { cn } from '@/components/ui/cn';
import moment from 'moment';
import { X4bActivityStatus } from '@/services/signalr.service';
import { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import { fetchActivityRouteData } from '@/modules/concierge/map/useInitialRoute';

type ExpandedActivityProps = {
	data: X4bActivityDto
}

const RowExpansionTemplate = ({ data: act }: ExpandedActivityProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const [routingData, setRoutingData] = useState<GoogleMapsRoutingResponse | null>(null);
	const [isSendingReceipt, setIsSendingReceipt] = useState(false);

	// Setting trip id and order uuid in the LiveMap component using bind setters
	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);

	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (orderUuid: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	// Set the order UUID and fetch trip ID or initial route when the component mounts or activity data changes
	useEffect(() => {
		if (!act.XpressId) return;

		fetchActivityRouteData(
			act,
			act.Id,
			act.XpressId,
			setRoutingData,
			orderUuidSetterRef,
			tripIdSetterRef
		);
	}, [act.XpressId, act.Id, act.Status]);

	useEffect(() => {
		if (isOpen) {
			dialogRef.current?.showModal();
		} else {
			dialogRef.current?.close();
		}
	}, [isOpen]);

	const handleOpen = () => setIsOpen(true);
	const handleClose = () => setIsOpen(false);

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleOpen();
		}
	};
	const handleEmailReceipt = async () => {
		setIsSendingReceipt(true);
		try {
			const { data } = await axiosInstance.post<BaseResponse>(
				ENDPOINTS['/api/X4bActivity/EmailReceipt'],
				{ Id: act.Id }
			);
			if (data.Success) {
				showSuccess('Receipt email sent successfully');
			} else {
				showError(data.ErrorMessage || 'Failed to send receipt email');
			}
		} catch {
			showError('Failed to send receipt email');
		} finally {
			setIsSendingReceipt(false);
		}
	};

	let rideTime = '00:00:00';
	if (act.PickedUpTime && act.CompletedTime) {
		const duration = moment.duration(moment(act.CompletedTime).diff(act.PickedUpTime));
		rideTime = `${String(Math.floor(duration.asHours())).padStart(2, '0')}:${String(duration.minutes()).padStart(2, '0')}:${String(duration.seconds()).padStart(2, '0')}`;
	}

	return (
		<div className={styles.collapsibleContent}>
			<div className={styles.flexRowGap}>
				<div className="relative">
					<ConciergeLiveMap
						key={act.XpressId}
						bindTripIdSetter={bindTripIdSetter}
						bindOrderUuidSetter={bindOrderUuidSetter}
						pickupLocationName={act.Pickup?.PlaceName || act.Pickup?.Address || undefined}
						dropOffLocationName={act.Dropoff?.PlaceName || act.Dropoff?.Address || undefined}
						mapDivClassName={styles.imagePlaceholder}
						activity={act}
						markersAsDots={true}
						initialRoute={routingData}
					/>
					<button
						type="button"
						className={styles.expandFullScreenContainer}
						onClick={handleOpen}
						onKeyDown={handleKeyPress}
						aria-label="Expand map to fullscreen"
					>
						<MdFullscreen className={styles.fullscreenIcon} />
					</button>
				</div>

				<ExpandedMapModal isOpen={isOpen} handleClose={handleClose} data={act} />

				<div className={styles.rowDetails}>
					<div className={styles.rowInfo}>
						<div className={styles.rowInfoItem}>
							<p className={styles.rowInfoLabel}>Fare</p>
							<h2 className={styles.fare}>{formatCurrency(act.Amount)}</h2>
						</div>

						<div className={styles.rowInfoItem}>
							<p className={styles.rowInfoLabel}>Booking ID</p>
							<p className={styles.rowInfoValue}>{act.BookingId}</p>
						</div>

						<div className={styles.rowInfoItem}>
							<p className={styles.rowInfoLabel}>Time</p>
							<p className={styles.rowInfoValue}>{rideTime}</p>
						</div>

						<div className={styles.rowInfoItem}>
							<p className={styles.rowInfoLabel}>Distance</p>
							<p className={styles.rowInfoValue}>{Math.round((act.DistanceInM ?? 0) / 1000)}km</p>
						</div>
					</div>

					<LineDivider />

					<div className={styles.routeDetails}>
						<RouteDetails gap="gap-1" flex="flex-1" activity={act} />

						<div className={styles.memoSection}>
							<div className={styles.memoItem}>
								<p className={styles.memoLabel}>
									Internal Memo
								</p>
								<p className={styles.memoValue}>
									{act.Memo}
								</p>
							</div>

							<div className={`${styles.memoItem} mt-[20px]`}>
								<p className={styles.memoLabel}>
									Note to driver
								</p>
								<p className={styles.memoValue}>{act.Note}</p>
							</div>
						</div>
					</div>

					<div className={styles.userInfo}>
						<div className="flex flex-col min-w-0">
							<p className={styles.userName}>{act.Driver?.Name}</p>
							<p className={styles.userDetails}>
								{act.Driver?.Plate} · {act.Driver?.Vehicle}
							</p>
						</div>

						<div className={cn('relative w-[186px] h-[96px] shrink-0 tablet:w-[140px] tablet:h-[72px]')}>
							<img
								src={getVehicleTypeInfo(act.VehicleType)?.imageLargePath}
								alt="Vehicle"
								className={'w-full h-full'}
							/>

							<div className={styles.rideUserAvatar}>
								<img
									src="/images/svg/avatar-placeholder.svg"
									alt="User Avatar"
									className={styles.rideUserAvatarIcon}
								/>
							</div>
						</div>
					</div>

					<LineDivider />

					<div className={styles.buttonContainer}>
						<RoundedFilledButton
							id="email-receipt-button"
							className="general-filled-button"
							label={isSendingReceipt ? "Sending..." : "Email receipt"}
							disabled={isSendingReceipt}
							onClick={handleEmailReceipt}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RowExpansionTemplate;
